import { URL_PARAMS as MD_URL_PARAMS } from '@block-md/constants';
import { StaticMarkdown } from '@core/component/LexicalMarkdown/component/core/StaticMarkdown';
import { toast } from '@core/component/Toast/Toast';
import { useAuthor } from '@core/context/user';
import { buildSimpleEntityUrl } from '@core/util/url';
import {
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  type ParentProps,
  Show,
  untrack,
  useContext,
} from 'solid-js';
import { getAndClearCommentMentions } from '.';
import type { CommentId, Root, ThreadId } from './commentType';
import { EditInput } from './Inputs';
import { MessageTopRow } from './MessageTopRow';
import { CommentsContext, ThreadContext } from './Thread';

const ThreadLine = () => {
  return (
    <div class="w-px bg-edge h-full absolute left-3 translate-x-[-0.5px] top-4" />
  );
};

const CommentText = (props: { text: string; isThreaded?: boolean }) => {
  return (
    <div class="ml-6 pb-2">
      <StaticMarkdown markdown={props.text} />
    </div>
  );
};

function CommentContainer(
  props: ParentProps<{
    isThreaded?: boolean;
    isHighlighted?: boolean;
  }>
) {
  return (
    <div
      class="relative isolate group"
      classList={{
        'outline-1 outline-comment/20 -outline-offset-1': props.isHighlighted,
      }}
    >
      <div
        class="absolute top-0 left-0 size-[calc(100%)] rounded-lg -z-1"
        classList={{
          'bg-comment/5 opacity-100': props.isHighlighted,
          'bg-hover/50 opacity-0 group-hover:opacity-100': !props.isHighlighted,
        }}
      />
      <div
        class="supports-text-pretty:whitespace-normal wrap-break-word p-1"
        classList={{
          'pb-2': props.isThreaded,
        }}
      >
        {props.children}
      </div>
    </div>
  );
}

export function Comment(
  props: ParentProps<{
    comment: Root;
    isOwned: boolean;
    isActive: boolean;
    isThreaded?: boolean;
    actionsDropdown?: boolean;
  }>
) {
  const commentsContext = useContext(CommentsContext);

  const {
    commentOperations,
    setActiveThread,
    setMessageEditing,
    highlightedCommentId,
  } = commentsContext;
  const isHighlighted = createMemo(
    () => highlightedCommentId() === props.comment.id
  );

  const isResolved = createMemo(() => props.comment.resolved ?? false);
  const date = () => props.comment.createdAt;

  const [textValue, setTextValue] = createSignal<string>(props.comment.text);
  const [isEditing, setIsEditingSignal] = createSignal<boolean>(false);
  // Report each edit-state transition to the drawer host (it hides its
  // pinned reply composer while an edit is open). A wrapper instead of an
  // effect keeps the reporting explicit at every mutation site.
  const setIsEditing = (editing: boolean) => {
    setIsEditingSignal(editing);
    setMessageEditing?.(props.comment.id, editing);
  };

  createEffect(() => {
    if (!untrack(isEditing)) return;
    if (!props.isActive) {
      setIsEditing(false);
    }
  });

  // The report must also retract on unmount (deletion mid-edit, or the
  // drawer paging to another thread with an edit open).
  onCleanup(() => setMessageEditing?.(props.comment.id, false));

  const copyLink = async () => {
    const params: Record<string, string> = {};
    if (commentsContext.documentType !== 'pdf') {
      params[MD_URL_PARAMS.commentId] = props.comment.id.toString();
    }
    try {
      const url = buildSimpleEntityUrl(
        { type: commentsContext.documentType, id: commentsContext.documentId },
        params
      );
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch (_) {
      toast.failure('Could not copy link');
    }
  };

  const mentionsSignal = useContext(ThreadContext).mentionsSignal;

  return (
    <Show
      when={isEditing()}
      fallback={
        <CommentContainer
          isThreaded={props.isThreaded}
          isHighlighted={isHighlighted()}
        >
          <Show when={props.isThreaded}>
            <ThreadLine />
          </Show>
          <MessageTopRow
            isOwned={props.isOwned}
            isActive={props.isActive}
            actionsDropdown={props.actionsDropdown}
            authorId={props.comment.author}
            date={date()}
            isNew={false}
            isResolved={isResolved()}
            toggleResolve={undefined} // Hide resolve button
            deleteMessage={() =>
              commentOperations.deleteComment({
                commentId: props.comment.id,
              })
            }
            enableEditing={() => {
              // prevent unsetting editing state by setting active comment thread first
              setActiveThread(props.comment.threadId);
              setIsEditing(true);
            }}
            copyLink={copyLink}
          />
          <CommentText text={props.comment.text} />
          {props.children}
        </CommentContainer>
      }
    >
      <CommentContainer isThreaded={props.isThreaded}>
        <MessageTopRow
          isOwned={props.isOwned}
          isActive={props.isActive}
          isEditing
          actionsDropdown={props.actionsDropdown}
          authorId={props.comment.author}
          date={date()}
          isResolved={false}
          isNew={false}
          hideBottomMargin
        />
      </CommentContainer>
      <EditInput
        onSend={(newText: string) => {
          if (newText.trim() === '') return;
          setIsEditing(false);
          setTextValue(newText);
          return Promise.all([
            commentOperations.updateComment(props.comment.id, {
              text: newText,
              threadId: props.comment.threadId,
              mentions: getAndClearCommentMentions(mentionsSignal),
            }),
          ]);
        }}
        handleCancel={() => {
          setTextValue(textValue());
        }}
        deactivateThreadOnCancel={false}
        setEditing={setIsEditing}
        textValue={textValue()}
      />
      {/*tiny spacer*/}
      <div class="w-full h-2" />
    </Show>
  );
}

export function CommentReply(
  props: ParentProps<{
    hide?: boolean;
    replyId: CommentId;
    threadId: ThreadId;
    deleteReply: () => void;
    updateReply: (content: string) => unknown | Promise<unknown>;
    isOwned: boolean;
    isActive: boolean;
    isThreaded?: boolean;
    actionsDropdown?: boolean;
  }>
) {
  const thisAuthor = useAuthor();
  const {
    getCommentById,
    highlightedCommentId,
    documentId,
    documentType,
    setMessageEditing,
  } = useContext(CommentsContext);
  const reply = createMemo(() => getCommentById(props.replyId));
  const isHighlighted = createMemo(
    () => highlightedCommentId() === props.replyId
  );

  const copyLink = async () => {
    const params: Record<string, string> = {};
    if (documentType !== 'pdf') {
      params[MD_URL_PARAMS.commentId] = props.replyId.toString();
    }
    try {
      const url = buildSimpleEntityUrl(
        { type: documentType, id: documentId },
        params
      );
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch (_) {
      toast.failure('Could not copy link');
    }
  };

  const [isEditing, setIsEditingSignal] = createSignal<boolean>(false);
  // As in Comment: explicit reporting at each mutation site, retracted on
  // unmount.
  const setIsEditing = (editing: boolean) => {
    setIsEditingSignal(editing);
    setMessageEditing?.(props.replyId, editing);
  };
  const [textValue, setTextValue] = createSignal<string>('');

  createEffect(() => setTextValue(reply()?.text ?? ''));

  onCleanup(() => setMessageEditing?.(props.replyId, false));

  const authorId = createMemo(() => reply()?.author ?? thisAuthor() ?? '');
  const date = () => reply()?.createdAt;
  const isNew = createMemo(() => reply()?.isNew ?? true);

  return (
    <Show when={!props.hide && reply()}>
      <Show
        when={isEditing()}
        fallback={
          <CommentContainer
            isThreaded={props.isThreaded}
            isHighlighted={isHighlighted()}
          >
            <Show when={props.isThreaded}>
              <ThreadLine />
            </Show>
            <MessageTopRow
              authorId={authorId()}
              date={date()}
              isNew={isNew()}
              isResolved={false}
              deleteMessage={props.deleteReply}
              enableEditing={() => setIsEditing(true)}
              copyLink={copyLink}
              hideBottomMargin
              isOwned={props.isOwned}
              isActive={props.isActive}
              actionsDropdown={props.actionsDropdown}
            />
            <CommentText text={reply()?.text ?? ''} />
            {props.children}
          </CommentContainer>
        }
      >
        <CommentContainer isThreaded={props.isThreaded}>
          <MessageTopRow
            authorId={authorId()}
            date={date()}
            isNew={isNew()}
            isResolved={false}
            isEditing
            isOwned={props.isOwned}
            isActive={props.isActive}
            actionsDropdown={props.actionsDropdown}
          />
          <EditInput
            handleCancel={() => {
              setIsEditing(false);
              setTextValue(textValue);
            }}
            onSend={(newText: string) => {
              if (newText.trim() === '') return;
              const result = props.updateReply(newText);
              setIsEditing(false);
              setTextValue(newText);
              return result;
            }}
            deactivateThreadOnCancel={false}
            hidePadding
            isReply
            setEditing={setIsEditing}
            textValue={textValue()}
          />
          {/*tiny spacer*/}
          <div class="w-full h-1" />
        </CommentContainer>
      </Show>
    </Show>
  );
}
